import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  IconButton,
  Paper,
  Avatar,
  TextField,
  Divider,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button
} from '@mui/material';
import {
  Close as CloseIcon,
  Send as SendIcon,
  SmartToy as SmartToyIcon,
  Person as PersonIcon,
  DeleteOutline as DeleteIcon
} from '@mui/icons-material';
import api from '../../services/api';
import { saveMessages, loadMessages, clearMessages } from '../../utils/chatStorage';

export const DRAWER_WIDTH = 420;

export default function AIChat({ open, onClose, seanceId }) {
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const messagesContainerRef = useRef(null);

  // Load messages from storage when seanceId changes or drawer opens
  useEffect(() => {
    if (open && seanceId) {
      const savedMessages = loadMessages(seanceId);
      setMessages(savedMessages);
    }
  }, [open, seanceId]);

  // Save messages to storage whenever they change
  useEffect(() => {
    if (seanceId && messages.length > 0) {
      saveMessages(seanceId, messages);
    }
  }, [messages, seanceId]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isLoading]);

  // Focus input when drawer opens
  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 300);
    }
  }, [open]);

  const handleSendMessage = async () => {
    const userMessage = inputValue.trim();
    if (!userMessage || isLoading) return;

    // Add user message to conversation
    const newUserMessage = { role: 'user', content: userMessage };
    const updatedMessages = [...messages, newUserMessage];
    setMessages(updatedMessages);
    setInputValue('');
    setError(null);
    setIsLoading(true);

    try {
      // Send entire conversation to backend
      const response = await api.post('ia/chat', {
        messages: updatedMessages,
        model: 'gpt-4o',
        temperature: 1,
        max_tokens: null
      });

      // Add assistant response to conversation
      if (response.success && response.data?.message) {
        const assistantMessage = {
          role: 'assistant',
          content: response.data.message
        };
        setMessages([...updatedMessages, assistantMessage]);
      } else {
        throw new Error('Invalid response format from server');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      setError(error.message || 'Erreur lors de l\'envoi du message. Veuillez réessayer.');
      // Remove the user message if there was an error
      setMessages(messages);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleClearChat = () => {
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    setMessages([]);
    if (seanceId) {
      clearMessages(seanceId);
    }
    setDeleteDialogOpen(false);
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
  };

  if (!open) {
    return null;
  }

  return (
    <Box
      sx={{
        width: DRAWER_WIDTH,
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '-4px 0 20px rgba(0, 0, 0, 0.1)',
        height: 'calc(100vh - 64px)', // Account for Navigation bar
        borderLeft: '1px solid #e0e0e0',
        bgcolor: 'white',
        flexShrink: 0,
        overflow: 'hidden',
        position: 'sticky',
        top: 64, // Below Navigation bar
        alignSelf: 'flex-start',
        maxHeight: 'calc(100vh - 64px)'
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 2,
          borderBottom: '1px solid #e0e0e0',
          background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)', width: 40, height: 40 }}>
            <SmartToyIcon sx={{ fontSize: 24, color: 'white' }} />
          </Avatar>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
              Chat avec l'IA
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.9, fontSize: '0.75rem' }}>
              Conversation enregistrée
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {messages.length > 0 && (
            <Tooltip title="Effacer la conversation">
              <IconButton
                onClick={handleClearChat}
                size="small"
                sx={{ color: 'white', '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.2)' } }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <IconButton
            onClick={onClose}
            size="small"
            sx={{ color: 'white', '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.2)' } }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Messages Container */}
      <Box
        ref={messagesContainerRef}
        sx={{
          flex: 1,
          overflow: 'auto',
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          bgcolor: '#f8f9fa',
          '&::-webkit-scrollbar': {
            width: '8px'
          },
          '&::-webkit-scrollbar-track': {
            background: '#f1f1f1',
            borderRadius: '4px'
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#c1c1c1',
            borderRadius: '4px',
            '&:hover': {
              background: '#a8a8a8'
            }
          }
        }}
      >
        {messages.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              color: 'text.secondary',
              textAlign: 'center',
              px: 3
            }}
          >
            <Avatar
              sx={{
                bgcolor: '#e0e0e0',
                width: 80,
                height: 80,
                mb: 2
              }}
            >
              <SmartToyIcon sx={{ fontSize: 48, color: '#9e9e9e' }} />
            </Avatar>
            <Typography variant="h6" sx={{ mb: 1, fontWeight: 600, color: '#424242' }}>
              Commencez une conversation
            </Typography>
            <Typography variant="body2" sx={{ color: '#757575', maxWidth: 280 }}>
              Posez vos questions à l'IA. Votre conversation sera automatiquement enregistrée.
            </Typography>
          </Box>
        ) : (
          messages.map((message, index) => (
            <Box
              key={index}
              sx={{
                display: 'flex',
                gap: 1.5,
                justifyContent: message.role === 'user' ? 'flex-end' : 'flex-start',
                alignItems: 'flex-start',
                animation: 'fadeIn 0.3s ease-in'
              }}
            >
              {message.role === 'assistant' && (
                <Avatar
                  sx={{
                    bgcolor: '#10b981',
                    width: 36,
                    height: 36,
                    boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)'
                  }}
                >
                  <SmartToyIcon sx={{ fontSize: 20 }} />
                </Avatar>
              )}
              <Paper
                elevation={2}
                sx={{
                  p: 1.5,
                  maxWidth: '75%',
                  bgcolor: message.role === 'user' ? '#10b981' : 'white',
                  color: message.role === 'user' ? 'white' : 'text.primary',
                  borderRadius: message.role === 'user' 
                    ? '18px 18px 4px 18px' 
                    : '18px 18px 18px 4px',
                  wordBreak: 'break-word',
                  boxShadow: message.role === 'user'
                    ? '0 2px 8px rgba(16, 185, 129, 0.2)'
                    : '0 2px 8px rgba(0, 0, 0, 0.1)'
                }}
              >
                <Typography
                  variant="body1"
                  sx={{
                    whiteSpace: 'pre-wrap',
                    fontSize: '0.9rem',
                    lineHeight: 1.5
                  }}
                >
                  {message.content}
                </Typography>
              </Paper>
              {message.role === 'user' && (
                <Avatar
                  sx={{
                    bgcolor: '#757575',
                    width: 36,
                    height: 36
                  }}
                >
                  <PersonIcon sx={{ fontSize: 20 }} />
                </Avatar>
              )}
            </Box>
          ))
        )}
        {isLoading && (
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
            <Avatar
              sx={{
                bgcolor: '#10b981',
                width: 36,
                height: 36,
                boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)'
              }}
            >
              <SmartToyIcon sx={{ fontSize: 20 }} />
            </Avatar>
            <Paper
              elevation={2}
              sx={{
                p: 1.5,
                bgcolor: 'white',
                borderRadius: '18px 18px 18px 4px',
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
            >
              <CircularProgress size={16} sx={{ color: '#10b981' }} />
              <Typography variant="body2" sx={{ color: '#757575', fontSize: '0.85rem' }}>
                L'IA réfléchit...
              </Typography>
            </Paper>
          </Box>
        )}
        {error && (
          <Box
            sx={{
              p: 1.5,
              bgcolor: '#ffebee',
              borderRadius: 2,
              border: '1px solid #ef5350',
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}
          >
            <Typography variant="body2" color="error" sx={{ fontSize: '0.85rem' }}>
              ⚠️ {error}
            </Typography>
          </Box>
        )}
        <div ref={messagesEndRef} />
      </Box>

      {/* Input Area */}
      <Box
        sx={{
          p: 2,
          borderTop: '1px solid #e0e0e0',
          bgcolor: 'white'
        }}
      >
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
          <TextField
            inputRef={inputRef}
            fullWidth
            multiline
            maxRows={4}
            placeholder="Tapez votre message..."
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={isLoading}
            variant="outlined"
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '20px',
                bgcolor: '#f5f5f5',
                '&:hover': {
                  bgcolor: '#eeeeee'
                },
                '&.Mui-focused': {
                  bgcolor: 'white'
                }
              }
            }}
          />
          <IconButton
            onClick={handleSendMessage}
            disabled={!inputValue.trim() || isLoading}
            sx={{
              bgcolor: '#10b981',
              color: 'white',
              width: 40,
              height: 40,
              '&:hover': {
                bgcolor: '#059669'
              },
              '&:disabled': {
                bgcolor: '#e0e0e0',
                color: '#9e9e9e'
              },
              transition: 'all 0.2s'
            }}
          >
            {isLoading ? (
              <CircularProgress size={20} sx={{ color: 'white' }} />
            ) : (
              <SendIcon />
            )}
          </IconButton>
        </Box>
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            textAlign: 'center',
            mt: 1,
            color: '#9e9e9e',
            fontSize: '0.7rem'
          }}
        >
          Appuyez sur Entrée pour envoyer, Maj+Entrée pour une nouvelle ligne
        </Typography>
      </Box>

      <style>
        {`
          @keyframes fadeIn {
            from {
              opacity: 0;
              transform: translateY(10px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
        `}
      </style>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCancelDelete}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)'
          }
        }}
      >
        <DialogTitle sx={{ 
          pb: 1,
          background: 'linear-gradient(135deg, #ef5350 0%, #e53935 100%)',
          color: 'white',
          borderRadius: '12px 12px 0 0'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <DeleteIcon sx={{ fontSize: 28 }} />
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              Effacer la conversation
            </Typography>
          </Box>
        </DialogTitle>
        
        <DialogContent sx={{ p: 3, pt: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Avatar
              sx={{
                bgcolor: '#ffebee',
                width: 56,
                height: 56
              }}
            >
              <DeleteIcon sx={{ fontSize: 32, color: '#e53935' }} />
            </Avatar>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                Êtes-vous sûr ?
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Cette action est irréversible
              </Typography>
            </Box>
          </Box>
          
          <Typography variant="body1" sx={{ color: '#424242', lineHeight: 1.6 }}>
            Tous les messages de cette conversation seront définitivement supprimés. 
            Vous ne pourrez pas récupérer cette conversation une fois supprimée.
          </Typography>
        </DialogContent>
        
        <DialogActions sx={{ p: 3, pt: 2, gap: 2 }}>
          <Button
            onClick={handleCancelDelete}
            variant="outlined"
            sx={{
              borderColor: '#d1d5db',
              color: '#6b7280',
              borderRadius: '20px',
              px: 3,
              py: 1,
              fontWeight: 'bold',
              textTransform: 'none',
              '&:hover': {
                borderColor: '#9ca3af',
                bgcolor: '#f9fafb'
              }
            }}
          >
            Annuler
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            startIcon={<DeleteIcon />}
            sx={{
              background: 'linear-gradient(135deg, #ef5350 0%, #e53935 100%)',
              color: 'white',
              borderRadius: '20px',
              px: 3,
              py: 1,
              fontWeight: 'bold',
              textTransform: 'none',
              boxShadow: '0 4px 12px rgba(239, 83, 80, 0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #e53935 0%, #c62828 100%)',
                boxShadow: '0 6px 16px rgba(239, 83, 80, 0.4)'
              }
            }}
          >
            Effacer définitivement
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
